import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { TenantBrandingLocal } from '../api/types/TenantLocal';
import { isValidHsl } from '../lib/colorHelpers';

// Branding por defecto = el de "Fiado Platform" (tenantId 'platform', campo brandingConfig en
// PlatformTenantConfig_GT). Copiado exacto para que todo tenant sin branding propio (null) muestre
// la identidad de fiado platform en vez de un genérico. faviconUrl: platform usa un data-URL base64;
// acá apuntamos al mismo logo PNG (si se quiere el favicon exacto embebido, copiar el data-URL).
export const FIADO_DEFAULT_BRANDING: TenantBrandingLocal = {
    primaryColor: '0 0% 97%',
    secondaryColor: '241 71% 41%',
    accentColor: '319 80% 49%',
    logoUrl:
        'https://i0.wp.com/yoconfiado.com/wp-content/uploads/2023/08/cropped-Fiado.png?fit=1920%2C530&ssl=1',
    logoWidth: 56,
    faviconUrl:
        'https://i0.wp.com/yoconfiado.com/wp-content/uploads/2023/08/cropped-Fiado.png?fit=1920%2C530&ssl=1',
};

/**
 * Un branding cuenta como "custom" sólo si al menos un color es un HSL válido. Cuando la ficha
 * `PlatformTenantConfig_GT[...].branding` es null, el normalizer mete strings vacíos (`""`):
 * eso NO es branding → tratamos como "sin custom" para aplicar los defaults Fiado (lindos) en
 * vez de pintar la UI con valores rotos.
 */
export function hasCustomColors(b: TenantBrandingLocal): boolean {
    return [b.primaryColor, b.secondaryColor, b.accentColor].some(isValidHsl);
}

interface BrandingState {
    branding: TenantBrandingLocal;
    isCustom: boolean;
    applyTenantBranding: (b: TenantBrandingLocal) => void;
    resetToFiadoDefaults: () => void;
}

export const useBrandingStore = create<BrandingState>()(
    persist(
        (set) => ({
            branding: FIADO_DEFAULT_BRANDING,
            isCustom: false,
            applyTenantBranding: (b) => {
                // Branding vacío/null (campos "") → no es custom: caemos a los defaults Fiado
                // para no pintar la UI con HSL inválidos.
                if (!hasCustomColors(b)) {
                    set({ branding: FIADO_DEFAULT_BRANDING, isCustom: false });
                    return;
                }
                set({ branding: b, isCustom: true });
            },
            resetToFiadoDefaults: () => {
                set({ branding: FIADO_DEFAULT_BRANDING, isCustom: false });
            },
        }),
        {
            name: 'branding-cache',
            storage: createJSONStorage(() => sessionStorage),
        },
    ),
);
